/* eslint-disable react/jsx-props-no-spreading */
/**
 * Props spreading is required because the parent component is fully customizable
 * So, we need to pass all the props to the parent component
 * That are typed as React.HTMLAttributes<HTMLDivElement>, the correct
 * types for a div element.
 */
import { useDraggable, useDroppable } from '@dnd-kit/core';
import React from 'react';
import {
  FileExplorerGridItemCard as BaseGridItem,
  FileExplorerGridItemCardActions,
  FileExplorerGridItemCardBody,
  FileExplorerGridItemCardDescription,
  FileExplorerGridItemCardFooter,
  FileExplorerGridItemCardHeader,
  FileExplorerGridItemCardTitle,
} from './Common/FileExplorerGridItemCard';
import { FileIcon, FolderIcon } from './FileExplorerIcons';
import { DraggedItem } from './hooks/useFileDragAndDrop';
import { TreeFile, TreeFolder } from './services';

interface FolderItemProps extends Omit<React.ComponentProps<'div'>, 'ref'> {
  node: TreeFolder & { index: number };
  actions: React.ReactNode;
  selectedItems?: DraggedItem[];
}

export function FileExplorerGridFolderItem({
  node,
  actions,
  selectedItems = [],
  ...props
}: FolderItemProps) {
  const draggedItem: DraggedItem = {
    id: node.id,
    type: 'folder',
    name: node.name,
  };

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `folder-${node.id}`,
    data: {
      item: draggedItem,
      selectedItems,
    },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-drop-${node.id}`,
    data: {
      folder: node,
    },
  });

  const style = {
    cursor: isDragging ? 'grabbing' : 'pointer',
  };

  return (
    <div ref={setDropRef}>
      <BaseGridItem
        className="h-100"
        ref={setDragRef}
        title={node.name}
        style={style}
        data-is-drag-over={isOver ? 'true' : undefined}
        data-is-dragging={isDragging ? 'true' : undefined}
        {...attributes}
        {...listeners}
        {...props}
      >
        <FileExplorerGridItemCardHeader>
          <FileExplorerGridItemCardActions className="pb-0">
            {actions}
          </FileExplorerGridItemCardActions>
        </FileExplorerGridItemCardHeader>
        <FileExplorerGridItemCardBody className="flex-1 pt-0">
          <FolderIcon height={24} width={24} />
        </FileExplorerGridItemCardBody>
        <FileExplorerGridItemCardFooter className="h-100 justify-content-center">
          <FileExplorerGridItemCardTitle>{node.name}</FileExplorerGridItemCardTitle>
        </FileExplorerGridItemCardFooter>
      </BaseGridItem>
    </div>
  );
}

interface FileItemProps extends Omit<React.ComponentProps<'div'>, 'ref'> {
  node: TreeFile & { index: number };
  actions: React.ReactNode;
  selectedItems?: DraggedItem[];
}

export function FileExplorerGridFileItem({
  node,
  actions,
  selectedItems = [],
  ...props
}: FileItemProps) {
  const draggedItem: DraggedItem = {
    id: node.id,
    type: 'file',
    name: node.title,
  };

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `file-${node.id}`,
    data: {
      item: draggedItem,
      selectedItems,
    },
  });

  const style = {
    cursor: isDragging ? 'grabbing' : 'pointer',
  };

  return (
    <BaseGridItem
      onDoubleClick={() => window.open(node.file, '_blank')}
      ref={setDragRef}
      title={node.title}
      style={style}
      data-is-dragging={isDragging ? 'true' : undefined}
      {...attributes}
      {...listeners}
      {...props}
    >
      <FileExplorerGridItemCardHeader>
        <FileExplorerGridItemCardActions className="pb-0">
          {actions}
        </FileExplorerGridItemCardActions>
      </FileExplorerGridItemCardHeader>
      <FileExplorerGridItemCardBody className="flex-1 flex-shrink-0 pt-0">
        <FileIcon height={24} width={24} />
      </FileExplorerGridItemCardBody>
      <FileExplorerGridItemCardFooter>
        <FileExplorerGridItemCardTitle>{node.title}</FileExplorerGridItemCardTitle>
        <FileExplorerGridItemCardDescription
          title={`Actualizado el ${new Date(node.updated_at).toLocaleDateString('es-CL')}`}
        >
          Actualizado el
          {'  '}
          {new Date(node.updated_at).toLocaleDateString('es-CL')}
        </FileExplorerGridItemCardDescription>
      </FileExplorerGridItemCardFooter>
    </BaseGridItem>
  );
}
